import {z} from 'zod';
import {principalSchema} from '../auth/principal';
import {auditEventSchema, auditPageSchema, auditQuerySchema} from '../domain/audit';
import {callieCredentialsSchema, setCallieCredentialsSchema} from '../domain/callie-credentials';
import {eventExportSchema} from '../domain/event-export';
import {createOrganizationSchema, organizationSchema, updateOrganizationSchema} from '../domain/organization';
import {openadrMessageSchema} from '../domain/openadr-message';
import {pageSchemaOf, pagingSchema} from '../domain/pagination';
import {requestSchema} from '../domain/request';
import {submissionMemberDetailSchema, submissionSummarySchema} from '../domain/submission';
import {
  configureCongestionAreaSchema,
  congestionAreaSchema,
  congestionAreaWithClusterSchema,
  createCongestionAreaSchema,
  updateCongestionAreaSchema,
} from '../domain/congestion-area';
import {
  configureCongestionAreaClusterSchema,
  congestionAreaClusterSchema,
  congestionAreaClusterSummarySchema,
} from '../domain/congestion-area-cluster';
import {guardApplicationSchema} from '../domain/mw-guard';
import {
  cancelRequestSchema,
  decisionRequestSchema,
  listCongestionAreasQuerySchema,
  listRequestsQuerySchema,
  requestCountsQuerySchema,
  requestCountsSchema,
  previewRequestSchema,
  previewResponseSchema,
  solvieProfileSchema,
  submitRequestSchema,
} from '../domain/request-contract';
import {
  createUserSchema,
  passwordResetConfirmSchema,
  passwordResetRequestSchema,
  updateProfileSchema,
  updateUserSchema,
  userSchema,
} from '../domain/user';

/**
 * Every endpoint, declared once.
 *
 * Before this existed the path lived in two places — `infra/routes.ts` registered
 * `/api/organizations` and the frontend client wrote the same string again — so renaming
 * one deployed green and 404'd in the browser. The schemas were already shared; the paths
 * were not, which left the last hole in the contract exactly where nothing could catch it.
 *
 * Both sides now read this file: infra registers what it finds here and a `RouteName` it
 * has no handler for is a type error, and the frontend builds its URLs with `pathTo`.
 * The document in `docs/openapi.json` is generated from the same declaration.
 *
 * Deliberately not a framework. ts-rest or oRPC would do this and more, but they would
 * also take over the transport, and per-route lambdas with per-route IAM are worth more
 * than what a framework adds on top of the manifest.
 */

/** A path segment in braces is a parameter: `/api/users/{id}`. */
export type RoutePath = `/api/${string}`;

export interface Route {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: RoutePath;
  /** Body schema, for the endpoints that take one. */
  request?: z.ZodType;
  /** Query-string schema. Absent means the endpoint takes no parameters. */
  query?: z.ZodType;
  /** Absent means the endpoint answers 204 with no body. */
  response?: z.ZodType;
  /** The success status. Only the two creates differ from 200. */
  status?: 201;
  /**
   * No JWT authorizer. Only for endpoints a caller who cannot sign in must reach, which
   * today is the password reset and nothing else.
   */
  public?: true;
  /** One line for the generated OpenAPI document. */
  summary: string;
}

const organizationPage = pageSchemaOf(organizationSchema);
const userPage = pageSchemaOf(userSchema);
/**
 * A row of the overview is a submission, not a request: one wizard run may cover several
 * congestion areas and the design draws them as `Breukelen +4` (`domain/submission.ts`).
 * The path stays `/api/requests` — a submission is how requests are listed, not a
 * resource of its own, and every other verb here still addresses one request.
 */
const requestPage = pageSchemaOf(submissionSummarySchema);
const congestionAreaPage = pageSchemaOf(congestionAreaWithClusterSchema);

/**
 * One of the two people a request records. The name and the deletion travel together
 * because the reader needs both: after anonymisation the name column holds an identifier
 * rather than a person, and only `deletedAt` says so.
 */
const requestActorSchema = z.object({name: z.string(), deletedAt: z.coerce.date().nullable()});

/** The detail read is the only one that carries the profile the approver rules on. */
const requestDetailSchema = z.object({
  /**
   * The areas this request was submitted alongside, itself included — one wizard run is one
   * row on the overview (`domain/submission.ts`), and the detail screen is where a reader
   * moves between them. Always at least this request; a submission of one is a list of one,
   * which the screen draws as no switcher at all.
   */
  submission: z.array(submissionMemberDetailSchema).min(1),
  request: requestSchema.extend({
    /**
     * Null when the stored document is not a profile this version recognises — an older
     * request, or one written before a contract change. Advertising a profile and
     * carrying `{}` would have every reader dereference a curve that is not there.
     */
    profile: solvieProfileSchema.nullable(),
    /**
     * Solvie's answer before an MW guard cut it (ADR 0036), so the screen can draw what
     * was taken off. Null whenever nothing was cut, which is the ordinary case — the
     * profile above is then the whole story.
     */
    solverProfile: solvieProfileSchema.nullable(),
    /**
     * The ceilings that covered this request — the area's own and its cluster's — and what
     * they did. Null only when the area has no ceiling of its own and is in no cluster; a
     * cluster without a ceiling still travels, so a screen can tell "capped, and it fitted"
     * from "nothing caps this".
     */
    guard: guardApplicationSchema.nullable(),
    /**
     * Resolved server-side. A screen showing who submitted and who approved must not be
     * handed two user ids and left to look them up: that is a second request, a second
     * chance to show the wrong name, and it leaks nothing the reader may not see anyway.
     *
     * **An anonymised account still answers here**, carrying its `deletedAt`, and that is
     * the point: the row is kept precisely because `request.created_by` is
     * `ON DELETE RESTRICT` and the four-eyes trail is why (ADR 0018). Returning null would
     * erase from the screen the very record the row exists to preserve. The stored name is
     * an identifier by then, so the interface renders the pair rather than the string.
     *
     * **The submitter is never null.** `request.created_by` is `NOT NULL` and its foreign
     * key is `ON DELETE RESTRICT`, so the row it points at cannot go: anonymisation empties
     * a user, it does not remove one. Saying `nullable` here would advertise a case the
     * database forbids and buy every reader a branch they can never reach.
     *
     * The approver is nullable, because a request nobody has decided genuinely has none.
     */
    createdByUser: requestActorSchema,
    decidedByUser: requestActorSchema.nullable(),
  }),
  messages: z.array(openadrMessageSchema),
});

export const ROUTES = {
  requestPasswordReset: {
    method: 'POST',
    path: '/api/password-reset',
    request: passwordResetRequestSchema,
    public: true,
    summary: 'Ask for a password reset link. Never reveals whether the address was known.',
  },
  confirmPasswordReset: {
    method: 'POST',
    path: '/api/password-reset/confirm',
    request: passwordResetConfirmSchema,
    public: true,
    summary: 'Set a new password using the token from the mail.',
  },

  getMe: {
    method: 'GET',
    path: '/api/me',
    response: principalSchema,
    summary: 'The signed-in user.',
  },
  updateMe: {
    method: 'PATCH',
    path: '/api/me',
    request: updateProfileSchema,
    response: userSchema,
    summary: 'Change your own name or interface language.',
  },

  listOrganizations: {
    method: 'GET',
    path: '/api/organizations',
    query: pagingSchema,
    response: organizationPage,
    summary: 'Organizations, paged. Platform admins only.',
  },
  createOrganization: {
    method: 'POST',
    path: '/api/organizations',
    request: createOrganizationSchema,
    response: organizationSchema,
    status: 201,
    summary: 'Create an organization.',
  },
  getOrganization: {
    method: 'GET',
    path: '/api/organizations/{id}',
    response: organizationSchema,
    summary: 'One organization. Members may read their own.',
  },
  updateOrganization: {
    method: 'PATCH',
    path: '/api/organizations/{id}',
    request: updateOrganizationSchema,
    response: organizationSchema,
    summary: 'Rename an organization or change its status.',
  },
  deleteOrganization: {
    method: 'DELETE',
    path: '/api/organizations/{id}',
    summary: 'Delete an organization. Refused while it still has users.',
  },

  getCallieCredentials: {
    method: 'GET',
    path: '/api/organizations/{id}/callie-credentials',
    response: callieCredentialsSchema,
    summary: 'Which GOPACS dispatch client this organization publishes as. Platform admins only.',
  },
  setCallieCredentials: {
    method: 'PUT',
    path: '/api/organizations/{id}/callie-credentials',
    request: setCallieCredentialsSchema,
    response: callieCredentialsSchema,
    summary: 'Set the organization’s GOPACS client id and secret. The secret is never readable again.',
  },
  clearCallieCredentials: {
    method: 'DELETE',
    path: '/api/organizations/{id}/callie-credentials',
    response: callieCredentialsSchema,
    summary: 'Remove the organization’s GOPACS credential. Publishing for it then refuses.',
  },

  listUsers: {
    method: 'GET',
    path: '/api/users',
    query: pagingSchema,
    response: userPage,
    summary: 'Users, paged and scoped to the caller’s organization unless they are a platform admin.',
  },
  createUser: {
    method: 'POST',
    path: '/api/users',
    request: createUserSchema,
    response: userSchema,
    status: 201,
    summary: 'Invite a user. Sends them a temporary password.',
  },
  getUser: {
    method: 'GET',
    path: '/api/users/{id}',
    response: userSchema,
    summary: 'One user.',
  },
  updateUser: {
    method: 'PATCH',
    path: '/api/users/{id}',
    request: updateUserSchema,
    response: userSchema,
    summary: 'Change a user’s name, role or status.',
  },
  deleteUser: {
    method: 'DELETE',
    path: '/api/users/{id}',
    summary: 'Delete a user, here and in the identity provider.',
  },
  resetUserPassword: {
    method: 'POST',
    path: '/api/users/{id}/reset-password',
    summary: 'Issue a new temporary password and mail it to the user.',
  },

  listCongestionAreas: {
    method: 'GET',
    path: '/api/congestion-areas',
    query: listCongestionAreasQuerySchema,
    response: congestionAreaPage,
    summary: 'Congestion areas of the caller organization, the ones a request can be made against.',
  },
  createCongestionArea: {
    method: 'POST',
    path: '/api/congestion-areas',
    request: createCongestionAreaSchema,
    response: congestionAreaSchema,
    status: 201,
    summary: 'Configure a congestion area for an organization. Platform admin only.',
  },
  updateCongestionArea: {
    method: 'PATCH',
    path: '/api/congestion-areas/{id}',
    request: updateCongestionAreaSchema,
    response: congestionAreaSchema,
    summary: 'Change a congestion area. Its organization is not patchable.',
  },
  configureCongestionArea: {
    method: 'PATCH',
    path: '/api/congestion-areas/{id}/settings',
    request: configureCongestionAreaSchema,
    response: congestionAreaSchema,
    summary:
      "Turn a congestion area on or off, or set its own MW ceiling. The owning organization's administrator only.",
  },
  deleteCongestionArea: {
    method: 'DELETE',
    path: '/api/congestion-areas/{id}',
    summary: 'Remove a congestion area, unless a request still references it.',
  },
  listCongestionAreaClusters: {
    method: 'GET',
    path: '/api/congestion-area-clusters',
    response: z.array(congestionAreaClusterSummarySchema),
    summary: 'Clusters of congestion areas of the caller organization, with the MW guard over each.',
  },
  configureCongestionAreaCluster: {
    method: 'PATCH',
    path: '/api/congestion-area-clusters/{id}',
    request: configureCongestionAreaClusterSchema,
    response: congestionAreaClusterSchema,
    summary: "Set the MW guard over a cluster of congestion areas. The owning organization's administrator only.",
  },

  previewRequest: {
    method: 'POST',
    path: '/api/requests/preview',
    request: previewRequestSchema,
    response: previewResponseSchema,
    summary: 'Calculate a profile for a dispatch and keep it server-side under a short-lived id.',
  },
  submitRequest: {
    method: 'POST',
    path: '/api/requests',
    request: submitRequestSchema,
    /** One per previewed area, committed together or not at all. */
    response: z.array(requestSchema),
    status: 201,
    summary: 'Submit one request per previewed area. Carries no profile: the server holds it.',
  },
  listRequests: {
    method: 'GET',
    path: '/api/requests',
    query: listRequestsQuerySchema,
    response: requestPage,
    summary: 'Requests of the caller organization, nearest dispatch day first.',
  },
  getRequest: {
    method: 'GET',
    path: '/api/requests/{id}',
    response: requestDetailSchema,
    summary: 'One request with its profile and its messages.',
  },
  /**
   * What happened to one request, newest first — the Log tab of its screen.
   *
   * A read of the audit trail narrowed to this request, rather than the trail itself:
   * anyone who may read a request may read what was done to it, which is not the same
   * permission as reading an organization's whole history.
   */
  requestLog: {
    method: 'GET',
    path: '/api/requests/{id}/log',
    response: z.array(auditEventSchema),
    summary: 'What happened to one request, newest first.',
  },
  /**
   * The `FLEX` event as it was sent for one resource group, for an operator who has to
   * hand it to somebody outside this tool — a Service Provider asking what they received,
   * or a GOPACS handover. Its own route because the detail read deliberately leaves event
   * bodies out: they are unbounded `jsonb`, and a list of them would not fit the 1 MB the
   * Data API returns.
   *
   * Answered in the export envelope with a single entry, so a reader parses the same
   * document whether they asked for one event or for all of them.
   */
  getMessageEvent: {
    method: 'GET',
    path: '/api/requests/{id}/messages/{messageId}/event',
    response: eventExportSchema,
    summary: 'The OpenADR event sent for one resource group, in the export envelope.',
  },
  decideRequest: {
    method: 'POST',
    path: '/api/requests/{id}/decision',
    request: decisionRequestSchema,
    response: requestSchema,
    summary: 'Approve or refuse a request. Never the submitter, never a platform admin.',
  },
  /**
   * The three actions that are about the **whole flex request** — every congestion area one
   * wizard run covered — rather than about one of its areas.
   *
   * A reader approves a dispatch, not a third of one: approving three areas of five would
   * leave a half-dispatch nobody asked for, and a screen whose header could not say what
   * state it is in. The id in the path is any member; the submission is read from it.
   */
  decideSubmission: {
    method: 'POST',
    path: '/api/requests/{id}/submission/decision',
    request: decisionRequestSchema,
    response: z.array(requestSchema),
    summary: 'Approve or refuse every congestion area of one flex request.',
  },
  publishSubmission: {
    method: 'POST',
    path: '/api/requests/{id}/submission/publish',
    response: z.array(z.object({request: requestSchema, messages: z.array(openadrMessageSchema)})),
    summary: 'Queue the events of every congestion area of one flex request.',
  },
  cancelSubmission: {
    method: 'POST',
    path: '/api/requests/{id}/submission/cancel',
    request: cancelRequestSchema,
    response: z.array(requestSchema),
    summary: 'Withdraw every congestion area of one flex request.',
  },
  cancelRequest: {
    method: 'POST',
    path: '/api/requests/{id}/cancel',
    request: cancelRequestSchema,
    response: requestSchema,
    summary: 'Withdraw your own request while it is still awaiting approval.',
  },
  publishRequest: {
    method: 'POST',
    path: '/api/requests/{id}/publish',
    /**
     * The request and the messages it just wrote — deliberately not the detail shape: a
     * publish answers about the row it changed, and does not resolve who submitted it.
     */
    response: z.object({request: requestSchema, messages: z.array(openadrMessageSchema)}),
    summary: 'Send the request: one OpenADR message per resource group.',
  },

  retryRequest: {
    method: 'POST',
    path: '/api/requests/{id}/retry',
    /** The request only: the messages it queued again are read from the detail. */
    response: requestSchema,
    summary: 'Queue the failed messages of a publication to be sent again.',
  },

  requestCounts: {
    method: 'GET',
    /**
     * Shares a shape with `/api/requests/{id}`. API Gateway prefers the static segment
     * over the variable one, so `counts` reaches this route rather than being read as an
     * id — but the two are worth keeping next to each other, because a rename that made
     * this path dynamic would make the collision real.
     */
    path: '/api/requests/counts',
    /** Which tab the numbers are for: the chips count what that tab holds, not the other. */
    query: requestCountsQuerySchema,
    /** One number per filter on the list screen, so a row of chips is one round trip. */
    response: requestCountsSchema,
    summary: 'How many requests each filter on the list screen holds.',
  },

  listAuditEvents: {
    method: 'GET',
    path: '/api/audit-events',
    query: auditQuerySchema,
    response: auditPageSchema,
    summary: 'The audit trail, newest first, cursor-paged.',
  },
} as const satisfies Record<string, Route>;

export type RouteName = keyof typeof ROUTES;

/** The parameters a path needs, read off the path itself. */
type ParamsOf<P extends string> = P extends `${string}{${infer Name}}${infer Rest}`
  ? {[K in Name | keyof ParamsOf<Rest>]: string}
  : Record<never, string>;

/** The parameters a route needs. `Record<never, string>` for a path without any. */
export type RouteParams<R extends Route> = ParamsOf<R['path']>;

/** Whether a route's path has parameters at all, as a tuple to spread into a signature. */
export type RouteParamsArgs<R extends Route> = keyof RouteParams<R> extends never ? [] : [RouteParams<R>];

/**
 * Substitution itself. Takes a plain optional record, so a caller that has already had
 * its own parameters checked — `call` in the frontend — can reach it without a cast that
 * throws the guarantee away.
 *
 * A missing parameter throws rather than producing a URL with a literal `{id}` in it.
 * That is the backstop for JavaScript callers; TypeScript callers get `pathTo`.
 */
export const buildPath = (route: Route, params?: Record<string, string>): string =>
  route.path.replace(/\{(\w+)\}/g, (_match, name: string) => {
    // `hasOwn` rather than a bare index: a route naming a parameter `constructor` or
    // `__proto__` would otherwise read the prototype chain and interpolate an object.
    const value = params && Object.hasOwn(params, name) ? params[name] : undefined;
    if (value === undefined || value === '') throw new Error(`Missing path parameter '${name}' for ${route.path}`);
    return encodeURIComponent(value);
  });

/**
 * The concrete URL for a route, checked. Overload-free by design: a path with no
 * parameters takes no second argument, and a path with `{id}` will not compile without
 * one.
 */
export const pathTo = <R extends Route>(route: R, ...params: RouteParamsArgs<R>): string => buildPath(route, params[0]);

/** Every route, as a list, for the places that need to walk them. */
export const routeList = (): {name: RouteName; route: Route}[] =>
  Object.entries(ROUTES).map(([name, route]) => ({name: name as RouteName, route}));
